import { pick } from 'lodash';
import moment from 'moment-timezone';
import { Callsheet, CallsheetRecipient } from '../../../__types__';
import { buildTeamMemberCreateRecipients } from 'v5/features/callsheets/callsheetTeamMemberEmails';

export function getCreatePayload(data: any) {
  const teamMembers =
    data.team_members &&
    data.team_members.map(team_member => {
      let groupId = team_member.group_id;
      if (team_member.group_id && team_member.group_id.includes('ref-')) {
        groupId = {
          '#ref': `group-${team_member.group_id.replace('ref-', '')}`
        };
        team_member.group = groupId;
        delete team_member.group_id;
      }
      if (team_member.id.includes('temp')) {
        delete team_member.id;
      }
      return {
        ...team_member,
        recipients: buildTeamMemberCreateRecipients({
          contactId: team_member.contact_id,
          recipients: team_member.recipients
        })
      };
    });
  const groups =
    data.groups &&
    data.groups.map(group => {
      let groupId;
      if (group.id.includes('ref')) {
        groupId = group.id.replace('ref-', '');
        delete group.id;
      }
      return {
        ...group,
        '#id': `group-${groupId}`
      };
    });
  const externalRecipients: string[] = [];
  data.recipients &&
    data.recipients.map(recipient => {
      if (!recipient.contact_id) {
        return externalRecipients.push(
          pick(recipient, ['email_address']) as unknown as string
        );
      }
    });
  const callsheet = {
    ...data,
    team_members: teamMembers,
    groups: groups,
    recipients: externalRecipients,
    isClone: data.isClone || false
  };

  callsheet.from_date = callsheet.from_date.includes('T')
    ? callsheet.from_date.split('T')[0]
    : callsheet.from_date;

  callsheet.to_date =
    callsheet.to_date && callsheet.to_date.includes('T')
      ? callsheet.to_date.split('T')[0]
      : callsheet.to_date;
  return callsheet as Callsheet; // todo add converter types or simplify the app
}

export function getUpdatePayload(data, id) {
  // Only include other fields if they are present in the original data
  const allowedFields = [
    'attachments',
    'description',
    'locations',
    'note',
    'production_date',
    'call_time',
    'published',
    'schedule',
    'status',
    'timezone',
    'title',
    'billing',
    'social',
    'hard_drive',
    'client_logo',
    'user_id',
    'from_date',
    'to_date',
    'team_members',
    'groups',
    'sections',
    'points_of_contact',
    'linked_productions' // allowed to be updated, for example auto-unlink when removing a production-linked section
  ];

  const payload: any = pick(data, allowedFields);

  function pickTeamMemberData(team_member) {
    const allowedTeamMemberFields = [
      'call_time',
      'callsheet_id',
      'contact_details_visible',
      'contact_id',
      'display_name',
      'display_phone_number',
      'display_profile_picture',
      'display_role',
      'display_note',
      'group_id',
      'id',
      'note',
      'order'
    ];

    const picked = pick(team_member, allowedTeamMemberFields);

    Object.keys(picked).forEach(key => {
      if (picked[key] === undefined) {
        delete picked[key];
      }
    });

    return picked;
  }

  function cleanContact(contact) {
    return pick(contact, ['id', 'role', 'email', 'full_name', 'phone_number']);
  }

  function formatTeamMember(team_member) {
    const formatted = {
      ...team_member,
      ...(team_member.id
        ? {}
        : {
            recipients: [
              {
                contact_id: team_member.contact_id,
                callsheet_id: id
              }
            ]
          })
    };

    if ('call_time' in team_member && team_member.call_time !== undefined) {
      const momentCallTime = moment(team_member.call_time);
      formatted.call_time = momentCallTime.isValid()
        ? momentCallTime.format('HH:mm:ss')
        : team_member.call_time;
    }

    return formatted;
  }

  // Only process team_members if present
  if ('team_members' in payload) {
    payload.team_members = payload.team_members
      .map(pickTeamMemberData)
      .map(formatTeamMember);
  }

  // Only process groups if present
  if ('groups' in payload) {
    payload.groups = payload.groups.map(group => {
      if (typeof group.id === 'string' && group.id.includes('ref-')) {
        delete group.id;
      }
      return group;
    });
  }

  // Only process sections if present
  if ('sections' in payload) {
    payload.sections = payload.sections.map(section => {
      // Filter out ref- groups from sections.groups as they haven't been created yet
      // it is important to filter only if groups are passed in the payload
      const cleanedGroups = section.groups?.filter(g => {
        const id = typeof g === 'object' && 'id' in g ? g.id : g;
        return !(typeof id === 'string' && id.includes('ref-'));
      });

      return {
        id: section.id,
        callsheet_id: section.callsheet_id,
        name: section.name,
        description: section.description,
        order: section.order,
        metadata: section.metadata,
        groups: cleanedGroups,
        archived: section.archived
      };
    });
  }

  // Only process from_date if present
  if ('from_date' in payload) {
    if (payload.from_date && payload.from_date.includes('T')) {
      payload.from_date = payload.from_date.split('T')[0];
    }
  }

  // Only process to_date if present
  if ('to_date' in payload) {
    if (payload.to_date && payload.to_date.includes('T')) {
      payload.to_date = payload.to_date.split('T')[0];
    }
  }

  // Only process points_of_contact if present
  if ('points_of_contact' in payload) {
    payload.points_of_contact = (payload.points_of_contact ?? []).map(
      cleanContact
    );
  }

  return payload;
}

export function getUpdateRecipientsPayload(recipients: CallsheetRecipient[]) {
  function pickRecipientData(recipient) {
    return pick(recipient, [
      'email_address',
      'alt_name',
      'callsheet_id',
      'callsheet_team_member_id',
      'contact_id',
      'id'
    ]);
  }

  const payload = {
    recipients: recipients ? recipients.map(pickRecipientData) : null
  };
  return payload;
}
